import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import PropTypes from 'prop-types'
import { ArrowLeft, ChevronDown, Pencil, User as UserIcon } from 'lucide-react'

import { AdasBox, AdasGrid, AdasButton, AdasCard, AdasSearchMenu, AdasTextField, AdasTypography } from '@components/wrapper-components'
import { SuccessModal, ConfirmModal } from '@components/common'
import {
  BUTTONS_DISPLAY_LABEL,
  GENERIC_MESSAGES,
  getInvoiceSummaryUrl,
  getQuoteSummaryUrl,
  getWorkOrderSummaryUrl
} from '../../../constants'
import { useLocation, useHistory } from 'react-router-dom'
import {
  createSupportTicket,
  getSupportTicketsConstants,
  getSupportTicket,
  getReferenceNumberBySearch,
  updateSupportTicketResolution,
  updateTicketStatus,
  updateTicketAssignee,
  uploadSupportPhoto,
  getSupportPhoto,
  updateSupportTicketDetails,
  getUserList
} from 'api/api'
import { Logger } from '../../../logger'
import { useMessageStore, useUserStore } from '../../../store'
import { useCenterStore } from '@caradasstore/CenterStore'
import { useLoadingStore } from '@caradasstore/LoadingStore'
import { ProblemAreaEnum, RaisedTypesEnum, StatusEnum, SupportRequestEnums, SupportRecordTypeEnums } from 'enums/enums'
import { PhotoInputWithoutThumbnail } from '@components/photo/photo-input-without-thumbnail'
import { DowngradeRequestPanel } from '@components/billing/DowngradeRequestPanel'

// Compact header pills that match the support-request design. They wire to the
// same handlers + APIs the larger SupportStatusInfo / AssignedUser components
// use, just rendered as small dropdown chips.
const STATUS_OPTIONS = [
  { value: 'New', bg: 'bg-[#E8EFFF]', text: 'text-[#1F6FEB]', dot: 'bg-[#1F6FEB]' },
  { value: 'Open', bg: 'bg-[#FEF3C7]', text: 'text-[#D97706]', dot: 'bg-[#D97706]' },
  { value: 'Closed', bg: 'bg-[#DCFCE7]', text: 'text-[#047857]', dot: 'bg-[#047857]' }
]

const getInitials = (name) => {
  if (!name) return ''
  const parts = String(name).trim().split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  return parts[0][0]?.toUpperCase() || ''
}

const StatusPill = ({ value, onChange, disabled }) => {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    if (!open) return undefined
    const onMouseDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [open])
  const current = STATUS_OPTIONS.find((o) => o.value.toLowerCase() === String(value || '').toLowerCase()) || STATUS_OPTIONS[0]
  return (
    <div ref={ref} className='relative inline-block'>
      <button
        type='button'
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[12px] font-bold ${current.bg} ${current.text} disabled:cursor-not-allowed`}
      >
        <span className={`h-1.5 w-1.5 rounded-full ${current.dot}`} />
        {current.value}
        {!disabled && <ChevronDown className='h-3 w-3' />}
      </button>
      {open && !disabled && (
        <div className='absolute right-0 z-30 mt-1 w-40 rounded-lg border border-[#D1D5D8] bg-white p-1 shadow-lg'>
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type='button'
              onClick={() => {
                onChange?.(opt.value)
                setOpen(false)
              }}
              className={`flex w-full items-center rounded px-2 py-1.5 text-left hover:bg-[#F8FAFC] ${
                opt.value === current.value ? 'bg-[#F1F4F8]' : ''
              }`}
            >
              <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-bold ${opt.bg} ${opt.text}`}>
                <span className={`h-1.5 w-1.5 rounded-full ${opt.dot}`} />
                {opt.value}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
StatusPill.propTypes = { value: PropTypes.string, onChange: PropTypes.func, disabled: PropTypes.bool }

const AssigneePill = ({ assignee, users, ticketId, onChange, disabled }) => {
  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    if (!open) return undefined
    const onMouseDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [open])
  const pick = async (next) => {
    if (saving) return
    setSaving(true)
    try {
      const res = await updateTicketAssignee({ id: ticketId, userId: next?.id || 0 })
      if (res?.status === 200) {
        onChange?.(next || null)
        setOpen(false)
      }
    } catch (err) {
      Logger.error({ message: err, payload: { file: 'AssigneePill', ticketId, next } })
    } finally {
      setSaving(false)
    }
  }
  const name = assignee?.name || ''
  const initials = getInitials(name)
  return (
    <div ref={ref} className='relative inline-block'>
      <button
        type='button'
        disabled={disabled || saving}
        onClick={() => setOpen((o) => !o)}
        className='inline-flex items-center gap-2 rounded-full bg-[#F1F4F8] px-2 py-1 text-[12px] font-semibold text-[#0A1628] disabled:cursor-not-allowed disabled:opacity-60'
      >
        <span className='inline-flex h-5 w-5 items-center justify-center rounded-full bg-white text-[10px] font-bold text-[#475569]'>
          {initials || <UserIcon className='h-3 w-3 text-[#6B7280]' />}
        </span>
        {name || 'Unassigned'}
        {!disabled && <Pencil className='h-3 w-3 text-[#6B7280]' />}
      </button>
      {open && !disabled && (
        <div className='absolute right-0 z-30 mt-1 max-h-72 w-56 overflow-y-auto rounded-lg border border-[#D1D5D8] bg-white p-1 shadow-lg'>
          <button
            type='button'
            onClick={() => pick(null)}
            className='flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-[13px] hover:bg-[#F8FAFC]'
          >
            <span className='inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#EBEDF0]'>
              <UserIcon className='h-3 w-3 text-[#6B7280]' />
            </span>
            Unassigned
          </button>
          {(users || []).map((u) => (
            <button
              key={u.id}
              type='button'
              onClick={() => pick(u)}
              className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-[13px] hover:bg-[#F8FAFC] ${
                assignee?.id === u.id ? 'bg-[#F1F4F8]' : ''
              }`}
            >
              <span className='inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#DBEAFE] text-[10px] font-bold text-[#1F2D3D]'>
                {getInitials(u.name)}
              </span>
              {u.name}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
AssigneePill.propTypes = {
  assignee: PropTypes.object,
  users: PropTypes.array,
  ticketId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  disabled: PropTypes.bool
}

export const CreateSupportRequestPage = () => {
  const location = useLocation()
  // problemDescription: optional pre-fill from the caller (e.g. Billing → Request downgrade).
  // downgradeToTier: set by Billing → Request downgrade; links the ticket to a downgrade request.
  const { selectedRecord = {}, requestType = '', supportType = '', problemDescription = '', downgradeToTier } = location?.state || {}
  const { ticketRequestId } = useParams()
  const isViewTicketRequest = ticketRequestId > 0
  const history = useHistory()
  const user = useUserStore((store) => store.currentUser)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [showCancleConfirmModal, setShowCancleConfirmModal] = useState(false)
  const [vehicleDetails, setVehicleDetails] = useState(selectedRecord)
  const [supportImage, setSupportImage] = useState([])
  const [supportRequest, setSupportRequest] = useState({
    dtc: '',
    dtc_description: '',
    calibration_type: '',
    type: supportType,
    device: '',
    browser: '',
    problem_description: problemDescription,
    problem_area:
      supportType === SupportRequestEnums.CALIBRATION_DIAGNOSTIC && requestType === SupportRecordTypeEnums.INFORMATION_REQUEST
        ? ProblemAreaEnum.OTHER
        : ProblemAreaEnum.DIAGNOSTIC_TROUBLE_CODE,
    contact_user_id: user,
    it_request_type: '',
    resolution: '',
    notes: ''
  })
  const [createdTicketId, setCreatedTicketId] = useState(null)
  const [openUpdateModal, setOpenUpdateModal] = useState(false)
  const [userList, setUserList] = useState([])
  const [errors, setErrors] = useState({
    notes: false
  })

  const refs = { notes: useRef(null) }
  const ccId = useCenterStore((store) => store.ccId)
  const setLoading = useLoadingStore((store) => store.setLoading)
  const addMessage = useMessageStore((store) => store.addMessage)
  const [supportRequestConstants, setSupportRequestConstants] = useState({})

  useEffect(() => {
    const fetchUserList = async () => {
      const userList = await getUserList({
        ccid: isViewTicketRequest && supportRequest.calibrationCenter?.id ? supportRequest.calibrationCenter?.id : ccId
      })

      setUserList(userList.data)
    }

    fetchUserList()
  }, [supportRequest])

  const isClosed = useMemo(() => {
    return StatusEnum[supportRequest?.status?.toLocaleUpperCase()] === StatusEnum.CLOSED
  }, [supportRequest.status])

  const isAdminEditEnabled = useMemo(() => {
    // Ticket CAR-2351 enabling add edit permission to admin
    return user.is_admin
      ? StatusEnum[supportRequest?.status?.toLocaleUpperCase()] === StatusEnum.OPEN || !isViewTicketRequest
      : !isViewTicketRequest
  }, [supportRequest.status, user.is_admin, isViewTicketRequest, ticketRequestId])

  const isNew = useMemo(() => {
    return StatusEnum[supportRequest?.status?.toLocaleUpperCase()] === StatusEnum.NEW
  }, [supportRequest.status])

  const isOpen = useMemo(() => {
    return StatusEnum[supportRequest?.status?.toLocaleUpperCase()] === StatusEnum.OPEN
  }, [supportRequest.status])

  const filteredUserList = useMemo(() => {
    if (!user.is_admin) {
      return userList.filter((userItem) => !userItem.is_admin)
    }
    return userList
  }, [userList])

  const MAX_LENGTH = 2000
  const MAX_LENGTH_DTC_DESC = 500
  const MAX_LENGTH_DTC_CODE = 40
  const MAX_LENGTH_PROBLEM_DESC = 500

  const handleInputChange = (e) => {
    const value = getValueBasedOnField(e.target.value, e.target.name)
    if (e.target.name === 'notes') {
      setErrors((prev) => ({ ...prev, notes: false }))
    }

    setSupportRequest((prev) => {
      const supportRequest = {
        ...prev,
        [e.target.name]: value
      }
      return supportRequest
    })
  }

  const getValueBasedOnField = (value, fieldName) => {
    let result
    let userSelected = ''
    switch (fieldName) {
      case 'contact_user_id':
        userSelected = filteredUserList.filter((user) => user.id === value)[0]
        result = userSelected
        break
      default:
        result = value
    }
    return result
  }

  const onCalibrationTypeChanged = (e) => {
    setSupportRequest((prev) => {
      const supportRequest = {
        ...prev,
        [e.target.name]: e.target.value
      }
      return supportRequest
    })
  }

  const handleCancel = useCallback(() => {
    if (user.is_admin) {
      return history.goBack()
    }

    setShowCancleConfirmModal(true)
  }, [])

  const handleDone = () => {
    history.replace('/support')
  }

  const handleGoBack = () => {
    history.replace('/support/support-request', { supportType, requestType, selectedRecord, enableButton: true })
  }

  const handleTicketStatusChange = async (value) => {
    try {
      setLoading(true)
      if (value === 'Closed') {
        const response = await getSupportTicket({ id: ticketRequestId })
        if (response?.status === 200) {
          if (!response?.data?.supportTicket?.notes) {
            setLoading(false)
            refs.notes.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
            refs.notes.current.focus()
            setErrors((prev) => ({ ...prev, notes: supportRequest.notes ? false : true }))

            return addMessage({
              text: supportRequest.notes
                ? 'Please save the notes before closing the ticket.'
                : 'A Closing Note is mandatory. Please add and update the ticket before closing the ticket.'
            })
          }
        }
      }

      const response = await updateTicketStatus({
        id: ticketRequestId,
        status: value
      })

      setLoading(false)
      if (response.status === 200) {
        setSupportRequest((prev) => ({
          ...prev,
          status: value
        }))
      }
    } catch (error) {
      setLoading(false)
      Logger.error({
        message: error,
        payload: { file: 'CreateSupportRequestPage', method: 'handleTicketStatusChange' }
      })
    }
  }

  const handleAsisignedUserChange = (user) => {
    setSupportRequest((prev) => {
      const supportRequest = {
        ...prev,
        assigned_to: user
      }
      return supportRequest
    })
  }

  const handleUpdateModal = useCallback(() => {
    setOpenUpdateModal((prevState) => !prevState)
  }, [setOpenUpdateModal])

  const handleUpdate = useCallback(
    async (updateType) => {
      if (openUpdateModal) {
        handleUpdateModal()
      }

      let supportDetails = {
        problem_description: supportRequest.problem_description
      }

      if (supportRequest.type === SupportRequestEnums.CALIBRATION_DIAGNOSTIC) {
        supportDetails = {
          ...supportDetails,
          problem_area: supportRequest.problem_area,
          dtc: supportRequest.dtc,
          dtc_description: supportRequest.dtc_description,
          calibration_type: supportRequest.calibration_type
        }
      } else if (supportRequest.type === SupportRequestEnums.CAR_ADASPLUS_TECHNOLOGY) {
        supportDetails = {
          ...supportDetails,
          device: supportRequest.device,
          browser: supportRequest.browser
        }
      }
      try {
        setLoading(true)
        const detailUpdateResponse = await updateSupportTicketDetails({ ticketId: supportRequest.id, data: supportDetails })
        if (detailUpdateResponse?.status === 200) {
          const response = await updateSupportTicketResolution({
            id: supportRequest.id,
            data: { resolution: supportRequest.resolution, notes: supportRequest.notes },
            updateType
          })
          if (response?.status === 200) {
            if (updateType === 'saveAndEmail') history.goBack()
          }
        }
      } catch (error) {
        addMessage({ text: 'Something went wrong! please try again.' })
        Logger.error({ message: error, payload: { file: 'createSupportRequestPage', method: 'Update support request', supportDetails } })
      } finally {
        setLoading(false)
      }
    },
    [supportRequest, updateSupportTicketResolution, history, handleUpdateModal, openUpdateModal]
  )

  const handleSave = useCallback(() => handleUpdate('saveOnly'), [handleUpdate])

  const handleContinueUpdateModal = useCallback(() => handleUpdate('saveAndEmail'), [handleUpdate])

  const handleSubmit = useCallback(async () => {
    let reqBody = ''
    setLoading(true)
    try {
      reqBody = {
        type: supportType,
        calibration_center_id: ccId,
        problem_description: supportRequest.problem_description,
        reference_number: selectedRecord.reference_number,
        qwi_id: selectedRecord.qwi_id,
        raised_stage: RaisedTypesEnum[selectedRecord.table_name],
        contact_user_id: supportRequest.contact_user_id.id,
        ...(downgradeToTier ? { downgrade_to_tier: downgradeToTier } : {})
      }

      if (supportType === SupportRequestEnums.CALIBRATION_DIAGNOSTIC) {
        reqBody = {
          ...reqBody,
          record_type: requestType,
          problem_area: supportRequest.problem_area,
          dtc: supportRequest.dtc,
          dtc_description: supportRequest.dtc_description,
          calibration_type: supportRequest.calibration_type
        }
      } else if (supportType === SupportRequestEnums.CAR_ADASPLUS_TECHNOLOGY) {
        reqBody = {
          ...reqBody,
          it_request_type: requestType,
          device: supportRequest.device,
          browser: supportRequest.browser
        }
      }

      const response = await createSupportTicket(reqBody)
      if (response?.status === 200) {
        await Promise.all(
          supportImage.map(async (image) => {
            try {
              if (image) {
                await uploadSupportPhoto({
                  file: image.file,
                  attachmentNo: image.attachment_number,
                  ticketId: response.data.supportTicket.id
                })
              }
            } catch (uploadError) {
              Logger.error({
                message: uploadError,
                payload: { file: 'createSupportRequestPage', method: 'handleSubmit/uploadSupportPhoto', reqBody }
              })
            }
          })
        )
        setCreatedTicketId(response?.data?.supportTicket?.ticket_number)
        setShowSuccessModal(true)
      }
    } catch (error) {
      addMessage({ text: 'Something went wrong! please try again.' })
      Logger.error({ message: error, payload: { file: 'createSupportRequestPage', method: 'create support request', reqBody } })
    } finally {
      setLoading(false)
    }
  }, [supportRequest, createSupportTicket, supportImage, downgradeToTier])

  const cancelConfirmationContinue = useCallback(() => {
    setShowCancleConfirmModal(false)
    history.replace('/support')
  }, [])

  const filteredSupportProblemAreas = useMemo(() => {
    if (supportType === SupportRequestEnums.CALIBRATION_DIAGNOSTIC && requestType === SupportRecordTypeEnums.INFORMATION_REQUEST) {
      return supportRequestConstants?.supportProblemAreas?.filter((problem) => problem.slug === ProblemAreaEnum.OTHER)
    } else return supportRequestConstants?.supportProblemAreas
  }, [requestType, supportType, supportRequestConstants?.supportProblemAreas])

  useEffect(() => {
    const fetchData = async () => {
      try {
        const constants = await getSupportTicketsConstants()
        setSupportRequestConstants(constants.data)
      } catch (error) {
        Logger.error({ message: error, payload: { file: 'createSupportRequestPage', method: 'useEffect/getSupportTicketsConstants' } })
      }
    }

    fetchData()
  }, [])

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (supportRequest.qwi_id) {
          const referenceResponse = await getReferenceNumberBySearch({
            ccId,
            search: supportRequest.reference_number
          })
          if (referenceResponse.data && referenceResponse.data?.length > 0) {
            const vehicle = referenceResponse.data.find((item) => item.reference_number === supportRequest.reference_number)
            setVehicleDetails(vehicle)
          }
        }
      } catch (error) {
        Logger.error({ message: error, payload: { file: 'createSupportRequestPage', method: 'useEffect/getReferenceNumberBySearch' } })
      }
    }
    if (isViewTicketRequest && supportRequest.qwi_id) {
      fetchData()
    }
  }, [ccId, supportRequest.qwi_id])

  const isSubmitDisabled = useMemo(() => {
    const calibrationDiagnosticChecks =
      supportRequest.type === SupportRequestEnums.CALIBRATION_DIAGNOSTIC &&
      ((supportRequest.problem_area === ProblemAreaEnum.OTHER &&
        (!supportRequest.problem_description || !supportRequest.contact_user_id.id)) ||
        (supportRequest.problem_area === ProblemAreaEnum.DIAGNOSTIC_TROUBLE_CODE &&
          (!supportRequest.dtc ||
            !supportRequest.calibration_type ||
            !supportRequest.problem_description ||
            !supportRequest.contact_user_id.id)) ||
        (supportRequest.problem_area === ProblemAreaEnum.FAILED_CALIBRATION &&
          (!supportRequest.calibration_type || !supportRequest.problem_description || !supportRequest.contact_user_id.id)))

    const carAdasPlusItChecks =
      supportRequest.type === SupportRequestEnums.CAR_ADASPLUS_TECHNOLOGY &&
      (!supportRequest.problem_description || !supportRequest.contact_user_id.id)

    return calibrationDiagnosticChecks || carAdasPlusItChecks
  }, [supportRequest])

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      try {
        const response = await getSupportTicket({ id: ticketRequestId })
        setLoading(false)
        if (response?.status === 200) {
          setSupportRequest(response?.data?.supportTicket)
          setSupportImage(response?.data?.supportTicketPhoto)
        }
      } catch (error) {
        setLoading(false)
        Logger.error({
          message: error,
          payload: { file: 'createSupportRequestPage', method: 'useEffect/getSupportTicket', ticketRequestId }
        })
      }
    }
    if (ticketRequestId) {
      fetchData()
    }
  }, [ticketRequestId, supportRequest.status, setLoading])

  const fetchSupportImageById = useCallback(
    async (photoId) => {
      return await getSupportPhoto({ ticketId: ticketRequestId, photoId })
    },
    [ticketRequestId]
  )

  const updateSupportImage = useCallback((formData, imageLocation, imageUrl) => {
    setSupportImage((prev) => {
      const imageArr = [...prev]
      imageArr[imageLocation] = formData ? { file: formData, attachment_number: imageLocation, imageUrl } : null
      return imageArr
    })
  }, [])

  const handleRecordClick = useCallback(() => {
    const { table_name: tableName, reference_number: referenceNumber } = vehicleDetails
    if (tableName === 'invoice') {
      history.push(getInvoiceSummaryUrl(referenceNumber))
    } else if (tableName === 'workorder') {
      history.push(getWorkOrderSummaryUrl(referenceNumber))
    } else {
      history.push(getQuoteSummaryUrl(referenceNumber))
    }
  }, [vehicleDetails, history, getInvoiceSummaryUrl, getWorkOrderSummaryUrl, getQuoteSummaryUrl])

  return (
    <div className='mx-auto max-w-[1400px] py-6'>
      {isViewTicketRequest && (
        <>
          {/* Back link + header card (eyebrow + ticket # + created/submitted + status + assignee) */}
          <button
            type='button'
            onClick={handleCancel}
            className='mb-3 inline-flex items-center gap-1.5 text-[13px] font-medium text-[#1F6FEB] hover:underline'
          >
            <ArrowLeft className='h-4 w-4' />
            All Support Requests
          </button>
          <div className='mb-4 rounded-xl border border-[#EBEDF0] bg-white px-5 py-4'>
            <div className='flex flex-wrap items-start justify-between gap-4'>
              <div className='min-w-0'>
                <div className='text-[11px] font-bold uppercase tracking-[0.16em] text-[#6B7280]'>
                  Support Request{supportRequest.calibrationCenter?.name ? ` · ${supportRequest.calibrationCenter.name}` : ''}
                </div>
                <div className='mt-1 flex flex-wrap items-baseline gap-3'>
                  <h1 className='text-[28px] font-bold leading-tight text-[#0A1628]'>
                    #{supportRequest.ticket_number ?? supportRequest.id ?? ticketRequestId}
                  </h1>
                  {supportRequest.created_at && (
                    <span className='text-[13px] text-[#6B7280]'>
                      {isClosed ? 'Closed' : 'Created'}{' '}
                      {new Date(isClosed && supportRequest.closed_at ? supportRequest.closed_at : supportRequest.created_at).toLocaleString(
                        'en-US',
                        { month: 'long', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }
                      )}
                      {supportRequest.contact_user_id?.name && (
                        <>
                          {' · Submitted by '}
                          <span className='font-medium text-[#0A1628]'>{supportRequest.contact_user_id.name}</span>
                        </>
                      )}
                    </span>
                  )}
                </div>
              </div>
              <div className='flex flex-wrap items-end gap-6'>
                <div>
                  <div className='text-[11px] font-bold uppercase tracking-wide text-[#6B7280]'>Status</div>
                  <div className='mt-1'>
                    <StatusPill
                      value={supportRequest.status}
                      onChange={handleTicketStatusChange}
                      disabled={!user.is_admin || !supportRequest.assigned_to}
                    />
                  </div>
                </div>
                {user.is_admin && (
                  <div>
                    <div className='text-[11px] font-bold uppercase tracking-wide text-[#6B7280]'>Assigned To</div>
                    <div className='mt-1'>
                      <AssigneePill
                        assignee={supportRequest.assigned_to}
                        users={filteredUserList}
                        ticketId={supportRequest?.id}
                        onChange={handleAsisignedUserChange}
                        disabled={isClosed}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* A plan downgrade ticket: approve / cancel here (admins), or cancel (the center's owners and managers). */}
          <DowngradeRequestPanel
            ticketId={ticketRequestId}
            isAdmin={Boolean(user.is_admin)}
            canManage={Boolean(user.is_owner || user.is_manager)}
            onChanged={() => setSupportRequest((prev) => ({ ...prev, status: 'CLOSED' }))}
          />
        </>
      )}

      <div className={isViewTicketRequest ? 'grid grid-cols-1 gap-4 lg:grid-cols-[1fr_320px]' : ''}>
        <div>
          <AdasCard className='my-0 mx-0 py-4 px-4'>
            <AdasBox className='p-4 mb-[20px]'>
              {!user.is_admin && supportRequest.resolution && (
                <>
                  <AdasTypography variant='subtitle2' className='mb-[20px]'>
                    RESOLUTIONS
                  </AdasTypography>
                  <AdasBox className='p-[20px] mb-[20px] bg-[#CBF8C880]'>
                    <ul style={{ listStyleType: 'disc', paddingLeft: '20px', marginBottom: 0 }}>
                      <li key='resolution'>
                        <AdasTypography className='font-normal text-sm leading-[23px]'>{supportRequest.resolution}</AdasTypography>
                      </li>
                    </ul>
                  </AdasBox>
                </>
              )}
              <AdasTypography variant='subtitle2' className='mb-[20px]'>
                DETAILS
              </AdasTypography>
              <AdasGrid container spacing={2}>
                {supportRequest.type === SupportRequestEnums.CAR_ADASPLUS_TECHNOLOGY && (
                  <>
                    <AdasGrid item xs={12} sm={6} md={6} lg={6}>
                      <AdasSearchMenu
                        fullWidth
                        id='device'
                        select
                        label='Device (Optional)'
                        variant='outlined'
                        onChange={handleInputChange}
                        className='mb-[20px] mr-[15px]'
                        name='device'
                        disabled={!isAdminEditEnabled}
                        value={supportRequest.device}
                        options={(supportRequestConstants?.supportDevices || []).map((item) => ({
                          value: item.slug,
                          name: item.device
                        }))}
                      />
                    </AdasGrid>
                    <AdasGrid item xs={12} sm={6} md={6} lg={6}>
                      <AdasSearchMenu
                        fullWidth
                        id='browser'
                        select
                        label='Web Browser (Optional)'
                        variant='outlined'
                        onChange={handleInputChange}
                        className='mb-[20px] mr-[15px]'
                        name='browser'
                        disabled={!isAdminEditEnabled}
                        value={supportRequest.browser}
                        options={(supportRequestConstants?.supportBrowsers || []).map((item) => ({
                          value: item.slug,
                          name: item.browser
                        }))}
                      />
                    </AdasGrid>
                  </>
                )}
                {supportRequest.type === SupportRequestEnums.CALIBRATION_DIAGNOSTIC && (
                  <>
                    <AdasGrid item xs={12} sm={6} md={6} lg={6}>
                      <AdasSearchMenu
                        fullWidth
                        id='problem-area'
                        select
                        label='Problem Area'
                        variant='outlined'
                        onChange={handleInputChange}
                        className={`mb-[20px] mr-[15px]${isViewTicketRequest ? '' : ' bg-white'}`}
                        name='problem_area'
                        disabled={!isAdminEditEnabled}
                        value={supportRequest.problem_area}
                        options={(filteredSupportProblemAreas || []).map((item) => ({
                          value: item.slug,
                          name: item.problemArea
                        }))}
                      />
                    </AdasGrid>
                    {supportRequest.problem_area !== 'OTHER' && (
                      <>
                        <AdasGrid item xs={12} sm={6} md={6} lg={6}>
                          <AdasTextField
                            onChange={handleInputChange}
                            name='dtc'
                            id='dtc'
                            label={
                              supportRequest.problem_area === ProblemAreaEnum.FAILED_CALIBRATION
                                ? 'Diagnostic Trouble Code (Optional)'
                                : 'Diagnostic Trouble Code'
                            }
                            helperText={`${MAX_LENGTH_DTC_CODE} characters max, ${MAX_LENGTH_DTC_CODE - supportRequest.dtc?.length || 0} remaining`}
                            variant='outlined'
                            value={supportRequest.dtc}
                            fullWidth={true}
                            disabled={!isAdminEditEnabled}
                            className={!isViewTicketRequest ? 'bg-white' : ''}
                            InputLabelProps={{
                              shrink: true
                            }}
                            inputProps={{
                              maxLength: 40
                            }}
                          />
                        </AdasGrid>
                        <AdasGrid item xs={12}>
                          <AdasTextField
                            onChange={handleInputChange}
                            name='dtc_description'
                            className={`mb-[20px] mt-[10px]${isViewTicketRequest ? '' : ' bg-white'}`}
                            id='dtc_description'
                            label='Code Description (Optional)'
                            helperText={`${MAX_LENGTH_DTC_DESC} characters max, ${MAX_LENGTH_DTC_DESC - supportRequest.dtc_description?.length || 0} remaining`}
                            variant='outlined'
                            value={supportRequest.dtc_description}
                            multiline={true}
                            rows={3}
                            fullWidth={true}
                            disabled={!isAdminEditEnabled}
                            InputLabelProps={{
                              shrink: true
                            }}
                            inputProps={{
                              maxLength: MAX_LENGTH_DTC_DESC
                            }}
                          />
                        </AdasGrid>
                        <AdasGrid item xs={12}>
                          <AdasSearchMenu
                            fullWidth
                            id='calibration_type'
                            select
                            label='Calibration Type'
                            variant='outlined'
                            onChange={onCalibrationTypeChanged}
                            name='calibration_type'
                            className={`mb-[20px]${isViewTicketRequest ? '' : ' bg-white'}`}
                            disabled={!isAdminEditEnabled}
                            value={supportRequest.calibration_type}
                            options={(supportRequestConstants?.calibrationCategoryTypes || []).map((item) => ({
                              value: item.slug,
                              name: item.name
                            }))}
                          />
                        </AdasGrid>
                      </>
                    )}
                  </>
                )}
                <AdasGrid item xs={12}>
                  <AdasTextField
                    onChange={handleInputChange}
                    name='problem_description'
                    id='problem_description'
                    label='Problem Description'
                    variant='outlined'
                    className={`mb-[20px]${isViewTicketRequest ? '' : ' bg-white'}`}
                    helperText={`${MAX_LENGTH_PROBLEM_DESC} characters max, ${MAX_LENGTH_PROBLEM_DESC - supportRequest.problem_description?.length || 0} remaining`}
                    value={supportRequest.problem_description}
                    multiline={true}
                    rows={3}
                    fullWidth={true}
                    disabled={!isAdminEditEnabled}
                    InputLabelProps={{
                      shrink: true
                    }}
                    inputProps={{
                      maxLength: MAX_LENGTH_PROBLEM_DESC
                    }}
                  />
                </AdasGrid>
              </AdasGrid>
            </AdasBox>
            <AdasBox className='justify-between p-4 mb-[20px]'>
              <AdasTypography variant='subtitle2' className='mb-[20px]'>
                SUPPORT PHOTOS
              </AdasTypography>
              <AdasBox className='flex'>
                <AdasBox className='w-1/2 mr-[20px]'>
                  <PhotoInputWithoutThumbnail
                    title='Support Photo'
                    parentId={ticketRequestId}
                    fetchImage={fetchSupportImageById}
                    imageDataObj={supportImage[0]}
                    labelText='ADD PHOTO'
                    imageLocation='0'
                    onUpdate={updateSupportImage}
                    disabled={isViewTicketRequest}
                    allowDelete={true}
                  />
                </AdasBox>
                {(supportImage[0]?.attachment_number || supportImage[1]?.attachment_number) && (
                  <AdasBox className='w-1/2'>
                    <PhotoInputWithoutThumbnail
                      title='Additional Support Photo'
                      parentId={ticketRequestId}
                      fetchImage={fetchSupportImageById}
                      imageLocation='1'
                      labelText='ADD ADDITIONAL PHOTO'
                      imageDataObj={supportImage[1]}
                      onUpdate={updateSupportImage}
                      disabled={isViewTicketRequest}
                      allowDelete={true}
                    />
                  </AdasBox>
                )}
              </AdasBox>
            </AdasBox>

            <AdasBox className='p-4'>
              <AdasTypography variant='subtitle2' className='mb-[20px]'>
                CONTACT INFORMATION
              </AdasTypography>
              <AdasGrid container spacing={2}>
                <AdasGrid item xs={12}>
                  <AdasSearchMenu
                    fullWidth
                    id='name'
                    select
                    label='Name'
                    variant='outlined'
                    onChange={handleInputChange}
                    className={`w-[49%]${isViewTicketRequest ? '' : ' bg-white'}`}
                    name='contact_user_id'
                    disabled={isViewTicketRequest}
                    value={supportRequest?.contact_user_id?.id}
                    options={filteredUserList?.map((item) => ({
                      value: item.id,
                      name: item.name
                    }))}
                  />
                </AdasGrid>
                <AdasGrid item xs={12} sm={6} md={6} lg={6}>
                  <AdasTextField
                    fullWidth
                    id='email'
                    label='Email'
                    variant='outlined'
                    className='mr-[15px]'
                    name='email'
                    value={supportRequest.contact_user_id?.email}
                    disabled={true}
                    InputLabelProps={{
                      shrink: true
                    }}
                    inputProps={{
                      maxLength: 28
                    }}
                  />
                </AdasGrid>
                <AdasGrid item xs={12} sm={6} md={6} lg={6}>
                  <AdasTextField
                    name='phone'
                    id='phone'
                    label='Phone'
                    variant='outlined'
                    value={supportRequest.contact_user_id?.phone}
                    fullWidth={true}
                    disabled={true}
                    InputLabelProps={{
                      shrink: true
                    }}
                    inputProps={{
                      maxLength: 28
                    }}
                  />
                </AdasGrid>
                {user.is_admin && supportRequest?.id && (
                  <>
                    <AdasGrid item xs={12} sm={12} md={12} lg={12}>
                      <AdasTextField
                        fullWidth
                        id='ccname'
                        label='CC Name'
                        variant='outlined'
                        className='mr-[15px]'
                        name='ccName'
                        value={supportRequest?.calibrationCenter?.name}
                        disabled={true}
                        InputLabelProps={{
                          shrink: true
                        }}
                      />
                    </AdasGrid>
                    <AdasGrid item xs={12}>
                      <AdasTextField
                        onChange={handleInputChange}
                        name='resolution'
                        id='resolution'
                        label='Resolution (Visible to Technician)'
                        variant='outlined'
                        className={`my-[20px]${isClosed || isNew ? '' : ' bg-white'}`}
                        disabled={isClosed || isNew}
                        helperText={`${MAX_LENGTH} characters max, ${MAX_LENGTH - supportRequest.resolution?.length || 0} remaining`}
                        value={supportRequest.resolution}
                        multiline={true}
                        rows={3}
                        fullWidth={true}
                        InputLabelProps={{
                          shrink: true
                        }}
                        inputProps={{
                          maxLength: MAX_LENGTH
                        }}
                      />
                    </AdasGrid>
                    <AdasGrid item xs={12}>
                      {/* clarify notes */}
                      <AdasTextField
                        onChange={handleInputChange}
                        name='notes'
                        id='notes'
                        label='Closing Notes (Visible to Admins Only)'
                        variant='outlined'
                        className={!(isClosed || isNew) ? 'bg-white' : ''}
                        helperText={`${MAX_LENGTH} characters max, ${MAX_LENGTH - supportRequest.notes?.length || 0} remaining`}
                        value={supportRequest.notes}
                        disabled={isClosed || isNew}
                        multiline={true}
                        innerRef={refs.notes}
                        error={errors.notes}
                        rows={3}
                        fullWidth={true}
                        InputLabelProps={{
                          shrink: true
                        }}
                        inputProps={{
                          maxLength: MAX_LENGTH
                        }}
                      />
                    </AdasGrid>
                  </>
                )}
              </AdasGrid>
            </AdasBox>

            <AdasBox className='overflow-auto my-[20px] mx-0 px-4'>
              <AdasBox>
                {isViewTicketRequest ? (
                  user.is_admin ? (
                    <>
                      <AdasBox className='flex-col sm:flex-row gap-2 sm:gap-4 items-start sm:items-center sm:justify-end flex'>
                        <AdasBox className='flex-row sm:flex-row gap-4 flex'>
                          <AdasButton variant='outlined' aria-label='cancel' color='primary' onClick={handleCancel}>
                            {isClosed ? 'Go Back' : 'Cancel'}
                          </AdasButton>
                          <AdasButton
                            variant='contained'
                            type='submit'
                            disabled={isNew || isClosed || isSubmitDisabled}
                            aria-label='save'
                            color='primary'
                            onClick={handleSave}
                          >
                            Save
                          </AdasButton>
                        </AdasBox>
                        <AdasButton
                          variant='contained'
                          type='submit'
                          disabled={isNew || isClosed || isSubmitDisabled}
                          aria-label='Save & Send Email'
                          color='primary'
                          onClick={handleUpdateModal}
                        >
                          Save & Send Email
                        </AdasButton>
                      </AdasBox>
                    </>
                  ) : (
                    <AdasButton variant='contained' aria-label='cancel' color='primary' onClick={handleDone} className='mr-[10px]'>
                      DONE
                    </AdasButton>
                  )
                ) : (
                  <AdasBox className='flex-col sm:flex-row gap-2 sm:gap-4 items-start sm:items-center sm:justify-end flex'>
                    <AdasBox className='flex-row sm:flex-row gap-4 flex'>
                      <AdasButton variant='outlined' aria-label='cancel' color='primary' onClick={handleCancel}>
                        Cancel
                      </AdasButton>
                      <AdasButton
                        variant='outlined'
                        type='submit'
                        disabled={isViewTicketRequest}
                        aria-label='goback'
                        onClick={handleGoBack}
                      >
                        GO BACK
                      </AdasButton>
                    </AdasBox>
                    <AdasButton
                      variant='contained'
                      type='submit'
                      disabled={isViewTicketRequest || isSubmitDisabled}
                      aria-label='save'
                      color='primary'
                      onClick={handleSubmit}
                      className='mt-2 sm:mt-0'
                    >
                      SUBMIT REQUEST
                    </AdasButton>
                  </AdasBox>
                )}
              </AdasBox>
            </AdasBox>

            {showSuccessModal && (
              <SuccessModal
                open={showSuccessModal}
                requestNumber={createdTicketId}
                handleClose={() => {
                  setShowSuccessModal(false)
                  history.replace('/support')
                }}
              />
            )}
            {showCancleConfirmModal && (
              <ConfirmModal
                open={showCancleConfirmModal}
                handleClose={() => {
                  setShowCancleConfirmModal(false)
                }}
                handleContinue={cancelConfirmationContinue}
                title={GENERIC_MESSAGES.CANCEL_REQUEST_TITLE}
                message={GENERIC_MESSAGES.CONFIRM_CANCEL_REQUEST}
                cancelLabel={BUTTONS_DISPLAY_LABEL.NO}
                confirmLabel={BUTTONS_DISPLAY_LABEL.YES}
              />
            )}
            {openUpdateModal && (
              <ConfirmModal
                className='w-1/2'
                open={openUpdateModal}
                handleClose={handleUpdateModal}
                handleContinue={handleContinueUpdateModal}
                title={GENERIC_MESSAGES.CONFIRM_UPDATE}
                message={
                  <AdasTypography
                    variant='body1'
                    className='font-normal text-base text-left leading-5 my-[10px] mx-0 max-w-[450px] tracking-[0.15000000596046448px]'
                  >
                    {GENERIC_MESSAGES.SUPPORT_UPDATE_INFO}
                  </AdasTypography>
                }
                cancelLabel={BUTTONS_DISPLAY_LABEL.NO}
                confirmLabel={BUTTONS_DISPLAY_LABEL.YES}
              />
            )}
          </AdasCard>
        </div>

        {isViewTicketRequest && (
          <aside className='space-y-4'>
            <div className='rounded-xl border border-[#EBEDF0] bg-white p-5'>
              <div className='mb-3 text-[11px] font-bold uppercase tracking-[0.16em] text-[#6B7280]'>Request Info</div>
              <dl className='space-y-3 text-[13px]'>
                {supportRequest.type && (
                  <div>
                    <dt className='text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]'>Support Type</dt>
                    <dd className='mt-0.5 text-[#0A1628]'>
                      {supportRequestConstants?.supportTypes?.find((t) => t.slug === supportRequest.type)?.type || supportRequest.type}
                    </dd>
                  </div>
                )}
                {supportRequest.problem_area && (
                  <div>
                    <dt className='text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]'>Problem Area</dt>
                    <dd className='mt-0.5 text-[#0A1628]'>
                      {supportRequestConstants?.supportProblemAreas?.find((a) => a.slug === supportRequest.problem_area)?.problemArea ||
                        supportRequest.problem_area}
                    </dd>
                  </div>
                )}
                {supportRequest.calibrationCenter?.name && (
                  <div>
                    <dt className='text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]'>Calibration Center</dt>
                    <dd className='mt-0.5 text-[#0A1628]'>{supportRequest.calibrationCenter.name}</dd>
                  </div>
                )}
                {supportRequest.created_at && (
                  <div>
                    <dt className='text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]'>Created</dt>
                    <dd className='mt-0.5 text-[#0A1628]'>
                      {new Date(supportRequest.created_at).toLocaleString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true
                      })}
                    </dd>
                  </div>
                )}
                {supportRequest.updated_at && (
                  <div>
                    <dt className='text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]'>Last Update</dt>
                    <dd className='mt-0.5 text-[#0A1628]'>
                      {new Date(supportRequest.updated_at).toLocaleString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true
                      })}
                    </dd>
                  </div>
                )}
                {supportRequest.contact_user_id?.name && (
                  <div>
                    <dt className='text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]'>Submitted By</dt>
                    <dd className='mt-0.5 text-[#0A1628]'>{supportRequest.contact_user_id.name}</dd>
                  </div>
                )}
              </dl>
            </div>

            {vehicleDetails && vehicleDetails.reference_number && (
              <div className='rounded-xl border border-[#EBEDF0] bg-white p-5'>
                <div className='mb-3 text-[11px] font-bold uppercase tracking-[0.16em] text-[#6B7280]'>Related Vehicle</div>
                <div className='text-[15px] font-bold text-[#0A1628]'>
                  {[vehicleDetails.year, vehicleDetails.make, vehicleDetails.model].filter(Boolean).join(' ')}
                </div>
                {vehicleDetails.vin && (
                  <div className='mt-1 text-[12px] text-[#6B7280]'>
                    <span className='font-semibold uppercase tracking-wide'>VIN</span> ·{' '}
                    <span className='font-mono text-[#0A1628]'>{vehicleDetails.vin}</span>
                  </div>
                )}
                {vehicleDetails.reference_number && (
                  <div className='mt-0.5 text-[12px] text-[#6B7280]'>
                    <span className='font-semibold uppercase tracking-wide'>RO</span> ·{' '}
                    <span className='text-[#0A1628]'>{vehicleDetails.reference_number}</span>
                  </div>
                )}
                <button type='button' onClick={handleRecordClick} className='mt-3 text-[13px] font-medium text-[#1F6FEB] hover:underline'>
                  View vehicle calibration record →
                </button>
              </div>
            )}
          </aside>
        )}
      </div>
    </div>
  )
}
